import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';
import { Facebook, Instagram } from 'lucide-react';

const Header = ({ cartCount = 0, user = null, onCartClick, onAccountClick, onLogout, onSearch }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isOrderDropdownOpen, setIsOrderDropdownOpen] = useState(false);
  const [isLocationsDropdownOpen, setIsLocationsDropdownOpen] = useState(false);
  const [isMobileLocationsOpen, setIsMobileLocationsOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const navigationItems = [
  { label: 'Home', path: '/home-landing', icon: 'Home' },
  { label: 'Menu', path: 'https://drive.google.com/file/d/1IaCoBWg1awgR1K-mMhs7nYbQZgbLT0Fa/preview', icon: 'UtensilsCrossed' },
  { label: 'Reservations', path: '/reservations', icon: 'Calendar' },
  { label: 'Catering', path: '/catering', icon: 'ChefHat' },
  { label: 'Contact', path: '/contact', icon: 'Mail' }];


  const isActivePath = (path) => {
    return location?.pathname === path;
  };

  const handleNavigation = (path) => {
    if (path?.startsWith('http')) {
      window.open(path, '_blank');
    } else {
      navigate(path);
    }
    setIsMobileMenuOpen(false);
  };

  const handleOrderLocation = (location) => {
    // Navigate to POS ordering page for the selected location
    // Replace these URLs with your actual POS ordering page URLs
    if (location === 'durham') {
      window.open('https://online.skytab.com/d532ef127b6d5a39d4172cf8121d87bf', '_blank');
    } else if (location === 'chapel-hill') {
      window.open('https://online.skytab.com/5a689c513d0e59a5cb701768602d0256', '_blank');
    }
    setIsOrderDropdownOpen(false);
    setIsMobileMenuOpen(false);
  };

  const handleLocationShowcase = (location) => {
    // Navigate to location showcase pages
    if (location === 'durham') {
      navigate('/durham-location');
    } else if (location === 'chapel-hill') {
      navigate('/chapel-hill-location');
    }
    setIsLocationsDropdownOpen(false);
    setIsMobileMenuOpen(false);
  };

  const toggleMobileMenu = (e) => {
    e?.stopPropagation();
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event?.target?.closest('.mobile-menu') && !event?.target?.closest('.mobile-menu-button')) {
        setIsMobileMenuOpen(false);
      }
      if (!event?.target?.closest('.order-dropdown') && !event?.target?.closest('.order-button')) {
        setIsOrderDropdownOpen(false);
      }
      if (!event?.target?.closest('.locations-dropdown') && !event?.target?.closest('.locations-button')) {
        setIsLocationsDropdownOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  return (
    <header className={`fixed top-[36px] left-0 right-0 z-50 border-b border-border/50 shadow-warm transition-colors duration-300 ${
    isMobileMenuOpen ? 'bg-card' : 'md:bg-card/30 md:backdrop-blur-md bg-card'}`
    }>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <div className="flex-shrink-0">
            <button
              onClick={() => handleNavigation('/home-landing')}
              className="flex items-center space-x-2 group">

              <div className="w-10 h-10 rounded-lg flex items-center justify-center group-hover:scale-105 transition-transform duration-200 overflow-hidden">
                <img src="/assets/images/logo_2_png_edited-1767119368116.png" alt="Kahlovera Street Tacos Logo" className="w-full h-full object-contain" />
              </div>
              <div className="text-left">
                <h1 className="text-xl font-heading font-bold text-primary">Kahlovera</h1>
                <p className="text-xs font-caption text-foreground/70 -mt-1">Mexican Bar & Grill</p>
              </div>
            </button>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-6">
            {/* Home */}
            <button
              onClick={() => handleNavigation(navigationItems?.[0]?.path)}
              className={`px-3 py-2 rounded-lg text-sm font-body font-medium transition-all duration-200 hover:bg-muted hover:scale-102 ${
              isActivePath(navigationItems?.[0]?.path) ?
              'text-primary bg-primary/10' : 'text-foreground hover:text-primary'}`
              }>

              <span>{navigationItems?.[0]?.label}</span>
            </button>

            {/* Locations Dropdown */}
            <div className="relative">
              <button
                onClick={() => setIsLocationsDropdownOpen(!isLocationsDropdownOpen)}
                className="locations-button flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-body font-medium transition-all duration-200 hover:bg-muted hover:scale-102 text-foreground hover:text-primary">

                <span>Locations</span>
                <Icon name="ChevronDown" size={14} className={`transition-transform duration-200 ${isLocationsDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {/* Locations Dropdown Menu */}
              {isLocationsDropdownOpen &&
              <div className="locations-dropdown absolute left-0 mt-2 w-48 bg-card border border-border rounded-lg shadow-warm-lg overflow-hidden animate-scale-in">
                  <button
                  onClick={() => navigate('/Durham')}
                  className="w-full px-4 py-3 text-left text-sm font-body font-medium text-foreground hover:bg-muted hover:text-primary transition-colors duration-200">

                    Durham
                  </button>
                  <div className="border-t border-border"></div>
                  <button
                  onClick={() => navigate('/ChapelHill')}
                  className="w-full px-4 py-3 text-left text-sm font-body font-medium text-foreground hover:bg-muted hover:text-primary transition-colors duration-200">

                    Chapel Hill
                  </button>
                </div>
              }
            </div>

            {/* Menu */}
            <button
              onClick={() => handleNavigation(navigationItems?.[1]?.path)}
              className="px-3 py-2 rounded-lg text-sm font-body font-medium transition-all duration-200 hover:bg-muted hover:scale-102 text-foreground hover:text-primary">

              <span>{navigationItems?.[1]?.label}</span>
            </button>

            {/* Reservations */}
            <button
              onClick={() => handleNavigation(navigationItems?.[2]?.path)}
              className={`px-3 py-2 rounded-lg text-sm font-body font-medium transition-all duration-200 hover:bg-muted hover:scale-102 ${
              isActivePath(navigationItems?.[2]?.path) ?
              'text-primary bg-primary/10' : 'text-foreground hover:text-primary'}`
              }>

              <span>{navigationItems?.[2]?.label}</span>
            </button>

            {/* Catering */}
            <button
              onClick={() => handleNavigation(navigationItems?.[3]?.path)}
              className={`px-3 py-2 rounded-lg text-sm font-body font-medium transition-all duration-200 hover:bg-muted hover:scale-102 ${
              isActivePath(navigationItems?.[3]?.path) ?
              'text-primary bg-primary/10' : 'text-foreground hover:text-primary'}`
              }>

              <span>{navigationItems?.[3]?.label}</span>
            </button>

            {/* Contact */}
            <button
              onClick={() => handleNavigation(navigationItems?.[4]?.path)}
              className={`px-3 py-2 rounded-lg text-sm font-body font-medium transition-all duration-200 hover:bg-muted hover:scale-102 ${
              isActivePath(navigationItems?.[4]?.path) ?
              'text-primary bg-primary/10' : 'text-foreground hover:text-primary'}`
              }>

              <span>{navigationItems?.[4]?.label}</span>
            </button>
          </nav>

          {/* Desktop Right Section - Order Now Button */}
          <div className="hidden md:flex items-center space-x-4">
            <div className="relative">
              <button
                onClick={() => setIsOrderDropdownOpen(!isOrderDropdownOpen)}
                className="order-button flex items-center space-x-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-body font-semibold transition-all duration-200 hover:bg-primary/90 hover:scale-102 shadow-sm">

                <span>Order Now</span>
                <Icon name="ChevronDown" size={16} className={`transition-transform duration-200 ${isOrderDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {/* Dropdown Menu */}
              {isOrderDropdownOpen &&
              <div className="order-dropdown absolute right-0 mt-2 w-48 bg-card border border-border rounded-lg shadow-warm-lg overflow-hidden animate-scale-in">
                  <button
                  onClick={() => handleOrderLocation('durham')}
                  className="w-full px-4 py-3 text-left text-sm font-body font-medium text-foreground hover:bg-muted hover:text-primary transition-colors duration-200 flex items-center justify-between">

                    <span>Durham</span>
                    <Icon name="ExternalLink" size={14} className="text-muted-foreground" />
                  </button>
                  <div className="border-t border-border"></div>
                  <button
                  onClick={() => handleOrderLocation('chapel-hill')}
                  className="w-full px-4 py-3 text-left text-sm font-body font-medium text-foreground hover:bg-muted hover:text-primary transition-colors duration-200 flex items-center justify-between">

                    <span>Chapel Hill</span>
                    <Icon name="ExternalLink" size={14} className="text-muted-foreground" />
                  </button>
                </div>
              }
            </div>
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden flex items-center space-x-3">
            {/* Social Media Icons - Mobile Only */}
            <a
              href="https://www.instagram.com/kahloveramx/"
              target="_blank"
              rel="noopener noreferrer"
              className="p-2 rounded-lg text-foreground hover:text-[#E4405F] transition-colors duration-200"
              aria-label="Follow us on Instagram">

              <Instagram size={20} />
            </a>
            <a
              href="https://www.facebook.com/profile.php?id=61550308602004"
              target="_blank"
              rel="noopener noreferrer"
              className="p-2 rounded-lg text-foreground hover:text-[#1877F2] transition-colors duration-200"
              aria-label="Follow us on Facebook">

              <Facebook size={20} />
            </a>
            {/* Mobile Menu Toggle */}
            <button
              onClick={toggleMobileMenu}
              className="mobile-menu-button p-2 rounded-lg text-foreground hover:text-primary hover:bg-muted transition-all duration-200">

              <Icon name={isMobileMenuOpen ? "X" : "Menu"} size={20} />
            </button>
          </div>
        </div>
      </div>
      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen &&
      <div className="fixed inset-0 top-16 bg-black/50 z-40 md:hidden" onClick={() => setIsMobileMenuOpen(false)} />
      }
      {/* Mobile Menu Drawer */}
      <div className={`mobile-menu fixed top-16 right-0 h-full w-80 max-w-[90vw] bg-card border-l border-border shadow-warm-xl z-50 transform transition-transform duration-300 md:hidden ${
      isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'}`
      }>
        <div className="p-6">
          {/* Mobile Order Now Section - MOVED TO TOP */}
          <div className="mb-6">
            <h3 className="text-sm font-body font-semibold text-foreground mb-3">Order Now</h3>
            <div className="space-y-2">
              <button
                onClick={() => handleOrderLocation('durham')}
                className="w-full flex items-center justify-between px-4 py-3 bg-primary text-primary-foreground rounded-lg text-sm font-body font-semibold transition-all duration-200 hover:bg-primary/90">

                <span>Durham</span>
                <Icon name="ExternalLink" size={16} />
              </button>
              <button
                onClick={() => handleOrderLocation('chapel-hill')}
                className="w-full flex items-center justify-between px-4 py-3 bg-primary text-primary-foreground rounded-lg text-sm font-body font-semibold transition-all duration-200 hover:bg-primary/90">

                <span>Chapel Hill</span>
                <Icon name="ExternalLink" size={16} className="text-muted-foreground" />
              </button>
            </div>
          </div>

          {/* Mobile Navigation Items */}
          <nav className="space-y-2">
            {/* Home */}
            <button
              onClick={() => handleNavigation(navigationItems?.[0]?.path)}
              className={`w-full flex items-center px-4 py-3 rounded-lg text-left font-body font-medium transition-all duration-200 hover:bg-muted ${
              isActivePath(navigationItems?.[0]?.path) ?
              'text-primary bg-primary/10' : 'text-foreground hover:text-primary'}`
              }>

              <span>{navigationItems?.[0]?.label}</span>
            </button>

            {/* Locations Dropdown - Collapsible */}
            <div>
              <button
                onClick={() => setIsMobileLocationsOpen(!isMobileLocationsOpen)}
                className="w-full flex items-center justify-between px-4 py-3 rounded-lg text-left font-body font-medium transition-all duration-200 hover:bg-muted text-foreground hover:text-primary">

                <span>Locations</span>
                <Icon name="ChevronDown" size={16} className={`transition-transform duration-200 ${isMobileLocationsOpen ? 'rotate-180' : ''}`} />
              </button>
              {isMobileLocationsOpen &&
              <div className="ml-4 mt-2 space-y-2">
                  <button
                  onClick={() => handleNavigation('/Durham')}
                  className="w-full flex items-center space-x-3 px-4 py-2 rounded-lg text-left font-body font-medium transition-all duration-200 hover:bg-muted text-foreground hover:text-primary">

                    <span>Durham</span>
                  </button>
                  <button
                  onClick={() => handleNavigation('/ChapelHill')}
                  className="w-full flex items-center space-x-3 px-4 py-2 rounded-lg text-left font-body font-medium transition-all duration-200 hover:bg-muted text-foreground hover:text-primary">

                    <span>Chapel Hill</span>
                  </button>
                </div>
              }
            </div>

            {/* Menu */}
            <button
              onClick={() => handleNavigation(navigationItems?.[1]?.path)}
              className="w-full flex items-center px-4 py-3 rounded-lg text-left font-body font-medium transition-all duration-200 hover:bg-muted text-foreground hover:text-primary">

              <span>{navigationItems?.[1]?.label}</span>
            </button>

            {/* Reservations */}
            <button
              onClick={() => handleNavigation(navigationItems?.[2]?.path)}
              className={`w-full flex items-center px-4 py-3 rounded-lg text-left font-body font-medium transition-all duration-200 hover:bg-muted ${
              isActivePath(navigationItems?.[2]?.path) ?
              'text-primary bg-primary/10' : 'text-foreground hover:text-primary'}`
              }>

              <span>{navigationItems?.[2]?.label}</span>
            </button>

            {/* Catering */}
            <button
              onClick={() => handleNavigation(navigationItems?.[3]?.path)}
              className={`w-full flex items-center px-4 py-3 rounded-lg text-left font-body font-medium transition-all duration-200 hover:bg-muted ${
              isActivePath(navigationItems?.[3]?.path) ?
              'text-primary bg-primary/10' : 'text-foreground hover:text-primary'}`
              }>

              <span>{navigationItems?.[3]?.label}</span>
            </button>

            {/* Contact */}
            <button
              onClick={() => handleNavigation(navigationItems?.[4]?.path)}
              className={`w-full flex items-center px-4 py-3 rounded-lg text-left font-body font-medium transition-all duration-200 hover:bg-muted ${
              isActivePath(navigationItems?.[4]?.path) ?
              'text-primary bg-primary/10' : 'text-foreground hover:text-primary'}`
              }>

              <span>{navigationItems?.[4]?.label}</span>
            </button>
          </nav>

          {/* Call Now Section - Added after Contact */}
          <div className="mt-6 border-border border-t-0 border-solid pt-0">
            <h3 className="text-sm font-body font-semibold text-foreground mb-3">Call Now</h3>
            <div className="space-y-2">
              <button
                onClick={() => window.location.href = 'tel:+19849994537'}
                className="w-full flex items-center justify-between px-4 py-3 bg-primary text-primary-foreground rounded-lg text-sm font-body font-semibold transition-all duration-200 hover:bg-primary/90">

                <span>Chapel Hill</span>
                <Icon name="Phone" size={16} />
              </button>
              <button
                onClick={() => window.location.href = 'tel:+19199730028'}
                className="w-full flex items-center justify-between px-4 py-3 bg-primary text-primary-foreground rounded-lg text-sm font-body font-semibold transition-all duration-200 hover:bg-primary/90">

                <span>Durham</span>
                <Icon name="Phone" size={16} />
              </button>
            </div>
          </div>

          {/* Call Us Now Button - Centered at Bottom */}
          <div className="mt-8 pt-6">
            <button
              onClick={() => window.location.href = 'tel:+19849994537'}
              className="w-full flex items-center justify-center space-x-2 px-6 py-3 bg-primary text-primary-foreground rounded-lg text-sm font-body font-semibold transition-all duration-200 hover:bg-primary/90 shadow-sm">

              <Icon name="Phone" size={18} />
              <span>Call Us Now</span>
            </button>
          </div>
        </div>
      </div>
    </header>);

};

export default Header;